import { useState } from 'react';
import { API_URL, apiFetch } from '../utils/api';

type Preview = { total: number; matched: number; unmatched: number; ambiguous: number; invalid: number; duplicates: number; rows: Array<Record<string, unknown>> };

const aliases: Record<string, string[]> = {
  practo_id: ['practo id','appointment id','id'], patient_number: ['patient number','patient id','patient no'], patient_name: ['patient name','name'], phone: ['phone','mobile','phone number','patient mobile'],
  appointment_date: ['appointment date','date'], appointment_time: ['appointment time','time','scheduled at'], center: ['center','clinic','location'],
  status: ['status','appointment status'], record_type: ['record type','type'],
};
const norm = (value: unknown) => String(value ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ');
const CENTRES = ['Bhubaneswar', 'Berhampur', 'Angul'];

export default function PractoImportPanel() {
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [center, setCenter] = useState('');

  async function choose(file: File) {
    setBusy(true); setMessage('Reading file…'); setPreview(null);
    try {
      const XLSX = await import('xlsx');
      const bytes = await file.arrayBuffer();
      const book = XLSX.read(bytes, { type: 'array', cellDates: false });
      const source = XLSX.utils.sheet_to_json<Record<string, unknown>>(book.Sheets[book.SheetNames[0]], { defval: '' });
      const rows = source.map((row, index) => {
        const entries = Object.entries(row);
        const output: Record<string, unknown> = { source_row_number: index + 2 };
        for (const [target, names] of Object.entries(aliases)) {
          const entry = entries.find(([header]) => names.includes(norm(header)));
          output[target] = entry?.[1] ?? '';
        }
        // Practo's appointment export has no centre or status column: the
        // uploader picks the centre, and a check-in/engaged/checkout time is
        // Practo's own record that the patient came.
        if (!output.center) output.center = center;
        if (!output.status) {
          const came = entries.some(([header, value]) => ['check in at', 'engaged at', 'checkout at'].includes(norm(header)) && String(value).trim());
          output.status = came ? 'checked in' : 'booked';
        }
        return output;
      });
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      const file_sha256 = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
      const next = { file_name: file.name, file_size: file.size, file_sha256, rows };
      const response = await apiFetch(`${API_URL}/api/admin/practo-import/preview`, { method: 'POST', body: JSON.stringify(next) });
      if (!response.ok) throw new Error(await response.text());
      setPayload(next); setPreview(await response.json()); setMessage('Preview ready. No CRM data changed.');
    } catch (e) { setMessage(String(e)); } finally { setBusy(false); }
  }

  async function commit() {
    if (!payload || !preview) return;
    if (!confirm('Commit this preview to CRM? This is idempotent but changes matched lead milestones.')) return;
    setBusy(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/practo-import/commit`, { method: 'POST', body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(await response.text());
      const result = await response.json() as { batch_id: string; replay?: boolean };
      setMessage(`${result.replay ? 'Already committed' : 'Committed'} batch ${result.batch_id}`);
    } catch (e) { setMessage(String(e)); } finally { setBusy(false); }
  }

  return <div className="space-y-3">
    <div><h3 className="font-semibold">Daily Practo reconciliation</h3><p className="text-xs text-slate-500">CSV/XLS/XLSX, maximum 500 rows and 2 MB. Preview first; phone matches are exact.</p></div>
    <select value={center} onChange={(e) => setCenter(e.target.value)} disabled={busy} className="block w-full rounded border p-2 text-sm">
      <option value="">Centre (if the file has no centre column)…</option>
      {CENTRES.map((name) => <option key={name}>{name}</option>)}
    </select>
    <input type="file" accept=".csv,.xls,.xlsx" disabled={busy} onChange={(e) => e.target.files?.[0] && void choose(e.target.files[0])} className="block w-full text-sm" />
    {message && <p className="rounded bg-slate-50 p-2 text-xs">{message}</p>}
    {preview && <>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">{(['total','matched','unmatched','ambiguous','invalid','duplicates'] as const).map((key) => <div key={key} className="rounded border p-2"><b className="block text-lg">{preview[key]}</b>{key}</div>)}</div>
      <button disabled={busy} onClick={() => void commit()} className="w-full rounded-lg bg-wechat-accent py-2 text-sm font-semibold text-white disabled:opacity-40">Commit clean rows</button>
    </>}
  </div>;
}
